import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { FiFilter, FiInfo, FiPlus, FiUser } from "react-icons/fi";
import { CommonTable, Loader } from "../../Components/Common";
import CommonFilter from "../../Components/Common/CommonFilter";
import * as routes from "../../routes";
import {
  RESET_PAGINATION,
  ROLES,
  USER_ROLES,
} from "../../Constants/Strings";
import { getLeaveCreditDebit } from "../../Services/LeaveServices";
import {
  creditTypeCSS,
  LEAVE_CATEGORY_OPTIONS,
  LEAVE_CREDIT_DEBIT_COLUMN_LABELS,
  LEAVE_CREDIT_DEBIT_COLUMNS,
  TOAST_MESSAGES,
} from "./constants";
import { formatDateDDMMYYYY, toProperCase } from "../../Utils";
import { UserContext } from "../../Context";

const LeaveCreditDebit = () => {
  const [LeaveCreditDebit, setLeaveCreditDebit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const location = useLocation();
  const [filters, setFilters] = useState(() => {
    return (
      location.state?.filters || {
        leave_credit_type: "",
        category: "",
        user: "",
      }
    );
  });
  const [selectedUserOption, setSelectedUserOption] = useState(() => {
    return location.state?.selectedUser || null;
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  useEffect(() => {
    fetchLeaveCreditDebit(
      { offset: 0, limit: pagination.limit },
      true,
      filters,
    );
  }, [filters]);

  const fetchLeaveCreditDebit = async (
    params,
    isInitial = false,
    currentFilters = filters,
  ) => {
    setLoading(true);
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([_, val]) => val !== ""),
      );

      const res = await getLeaveCreditDebit({ ...params, ...cleanedFilters });

      if (!res || !res.data) {
        throw new Error(TOAST_MESSAGES.NO_RESPONSE_DATA);
      }

      const results = res.data.results || [];
      setLeaveCreditDebit((prev) =>
        isInitial ? results : [...prev, ...results],
      );
      setPagination((prev) => ({
        ...prev,
        count: res.data.count || 0,
        offset: isInitial ? results.length : prev.offset + results.length,
      }));
    } catch (err) {
      toast.error(TOAST_MESSAGES.FAILED_FETCH_LEAVES);
      if (isInitial) {
        setLeaveCreditDebit([]);
        setPagination((prev) => ({ ...prev, count: 0, offset: 0 }));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreLeaveCreditDebit = () => {
    if (pagination.offset >= pagination.count) return;
    fetchLeaveCreditDebit({
      offset: pagination.offset,
      limit: pagination.limit,
    });
  };

  const handleApplyFilters = () => {
    setSelectedUserOption(tempFilters.user);
    tempFilters.user = tempFilters.user?.id;
    setFilters(tempFilters);
    setPagination(RESET_PAGINATION);
    setLeaveCreditDebit([]);
    setShowFilter(false);

    // Update navigation state for back button
    navigate(".", {
      state: { filters: tempFilters, selectedUser: tempFilters.user },
      replace: true,
    });
  };

  const handleClearFilters = () => {
    const cleared = { leave_credit_type: "", user: "", category: "" };
    setTempFilters(cleared);
    setFilters(cleared);
    setPagination(RESET_PAGINATION);
    setLeaveCreditDebit([]);
    setSelectedUserOption(null);
    navigate(".", { state: { filters: cleared }, replace: true });
  };

  const handleCancelFilters = () => {
    setTempFilters(filters);
    setShowFilter(false);
  };

  const handleRenderField = (key, row) => {
    if (key === "category") return toProperCase(row.category) + " Leave" || "-";
    if (key === "month") {
      if (!row.start_date) return "-";
      return new Date(row.start_date).toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
    if (key === "user") return row.user_name || "-";
    if (key === "leave_credit_type")
      return toProperCase(row.leave_credit_type) || "-";
    if (key === "leave_credit_count") return row.leave_credit_count || 0;

    if (key === "actions") {
      return (
        <div
          className={`grid grid-cols-1 items-center justify-center text-xl text-center`}
        >
          <FiInfo
            className="text-green-600 text-xl cursor-pointer"
            onClick={() =>
              navigate(
                routes.VIEW_LEAVE_CREDIT_DEBIT.replace(":instanceId", row.id),
                {
                  state: { filters, fromPage: routes.LEAVE_CREDIT_DEBIT_LIST },
                }
              )
            }
            title={"View"}
          />
        </div>
      );
    }
    return row[key] || "-";
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">
          Leave Credit and Debit
        </h2>

        <div className="flex gap-2 items-center">
          <button
            className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
            onClick={() => {
              setTempFilters(filters);
              setShowFilter(true);
            }}
          >
            <FiFilter className="text-xl" />
          </button>

          {(user.role === USER_ROLES.HR || USER_ROLES.DIRECTOR_MANAGEMENT.includes(user?.role)) && (
            <button
              onClick={() => navigate(routes.ADD_LEAVE_CREDIT_DEBIT)}
              className="bg-primary text-white px-4 py-2 rounded-md hover:bg-green-600 text-sm hidden md:block"
            >
              + Manage credit
            </button>
          )}

          {(user.role === USER_ROLES.HR || USER_ROLES.DIRECTOR_MANAGEMENT.includes(user?.role)) && (
            <button
              onClick={() => navigate(routes.ADD_LEAVE_CREDIT_DEBIT)}
              className="bg-primary text-white px-4 py-2 rounded-md hover:bg-green-600 text-sm block md:hidden"
            >
              <FiPlus className="text-xl" />
            </button>
          )}
        </div>
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <CommonFilter
          title="Leave Credit/Debit Filter"
          tempFilters={tempFilters}
          setTempFilters={setTempFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onCancel={handleCancelFilters}
          categoryOptions={[
            { label: "All", value: "" },
            ...LEAVE_CATEGORY_OPTIONS,
          ]}
          useSearchableUser={user.role === ROLES.HR ? true : false}
          selectedSearchableValue={selectedUserOption}
        />
      )}

      {/* Loader or Data */}
      {loading && LeaveCreditDebit.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Loader size={4} />
        </div>
      ) : LeaveCreditDebit.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          No Leaves Available
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block">
            <InfiniteScroll
              dataLength={LeaveCreditDebit.length}
              next={loadMoreLeaveCreditDebit}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="desktopLeaveCreditDebit"
            >
              <div
                id="desktopLeaveCreditDebit"
                className="h-[68vh] overflow-y-auto border rounded-md"
              >
                <CommonTable
                  tableColumns={LEAVE_CREDIT_DEBIT_COLUMNS}
                  tableRows={LeaveCreditDebit}
                  columnLabels={LEAVE_CREDIT_DEBIT_COLUMN_LABELS}
                  renderField={handleRenderField}
                />
              </div>
            </InfiniteScroll>
          </div>
        </>
      )}

      {/* MOBILE VIEW (Card UI) */}
      <div className="md:hidden">
        <InfiniteScroll
          dataLength={LeaveCreditDebit.length}
          next={loadMoreLeaveCreditDebit}
          hasMore={pagination.offset < pagination.count}
          scrollableTarget="mobileLeaveCreditDebit"
        >
          <div
            id="mobileLeaveCreditDebit"
            className="h-[75vh] overflow-y-auto space-y-2 p-1"
          >
            {LeaveCreditDebit.map((item) => {
              return (
                <div
                  key={item.id}
                  className="bg-white shadow-lg rounded-xl px-4 py-2 border border-gray-200 transition-all duration-300 hover:shadow-xl"
                  onClick={() =>
                    navigate(
                      routes.VIEW_LEAVE_CREDIT_DEBIT.replace(
                        ":instanceId",
                        item.id,
                      ),
                      {
                        state: {
                          filters,
                          fromPage: routes.LEAVE_CREDIT_DEBIT_LIST,
                        },
                      },
                    )
                  }
                >
                  {/* Header */}
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-lg text-gray-900 truncate max-w-[70%]">
                      {toProperCase(item.category)} Leave
                    </h3>
                    <div className="flex gap-2 text-xs">
                      <div
                        className={`px-2 py-1 rounded-md whitespace-nowrap ${
                          creditTypeCSS[item.leave_credit_type] ||
                          "bg-gray-100 text-gray-700"
                        }`}
                      >
                        <span className="font-semibold">
                          {toProperCase(item.leave_credit_type)}
                          {" - "}
                          {item.leave_credit_count}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="flex gap-1 mb-2">
                    <p className="text-sm text-gray-700 font-medium">
                      {item.start_date
                        ? new Date(item.start_date).toLocaleString("en-US", {
                            month: "long",
                            year: "numeric",
                          })
                        : "-"}
                    </p>
                  </div>

                  {/* User*/}
                  <div className="w-full flex justify-between items-center border-gray-100">
                    {/* User */}
                    <div className="w-[55%] flex items-center gap-2 text-gray-700 text-sm">
                      <FiUser size={18} className="text-gray-600" />
                      <span className="font-medium truncate">
                        {item.user_name || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default LeaveCreditDebit;
